import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { CSSTransition } from 'react-transition-group';
import { getIcon } from '@common/lib/getIcon';
import { toShortNumFormat } from '@common/lib/toShortNumFormat';
import { type ResourceTypeText } from '@common/types/resource';
import { resourceImages } from '@components/helpers/imagesImports';
import { storageTextObj } from '@components/helpers/ObjectsText';
import { Loader } from '@components/ui/Loader/Loader';
import { type SellParams, useModal } from '@context/ModalContext';
import { useAppDispatch, useAppSelector } from '@redux/store';

import { useSellStorageItemMutation } from '@/api/queries/storage';
import { defaultToastConfig } from '@/common/config/toast';
import { useGoToProduct } from '@/common/hooks/go-to-product';
import { playSound } from '@/common/lib/soundEffect';
import { timeStringFormat } from '@/common/lib/timer';
import ContractBackground from '@/components/common/ContractBackground/ContractBackground';
import { MainButton } from '@/components/ui/Button/Button';
import { CurrencyImage } from '@/components/ui/CurrencyImage/CurrencyImage';
import { Notification } from '@/components/ui/Notification/Notification';
import { Picture } from '@/components/ui/Picture/Picture';
import { setTimeToNextSell } from '@/redux/reducers/appReducer';
import { selectProductOfDayByGoodsType } from '@/redux/selectors/contractSelectors';

import cashPng from '/img/icons/cash.png';
import cashWebp from '/img/icons/cash.webp';

const ModalSellResource: React.FC = () => {
  const {
    openSellModal,
    setOpenSellModal,
    sellModalParams,
    setSellModalParams,
  } = useModal();
  const [sellStorageItem] = useSellStorageItemMutation();
  const [element, setElement] = useState<SellParams | null>(null);
  const [amount, setAmount] = useState(0);
  const [sliderValue, setSliderValue] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [initialAmount, setInitialAmount] = useState(0);
  const [targetSliderValue, setTargetSliderValue] = useState(0);
  const animationRef = useRef<number | undefined>(undefined);
  const goToProduct = useGoToProduct();

  const [activePercent, setActivePercent] = useState<number | null>(null);
  const [step, setStep] = useState(1);
  const [sellIsLoading, setSellIsLoading] = useState(false);
  const { t } = useTranslation();
  const timeToNextSell = useAppSelector((state) => state.app.timeToNextSell);

  const productOfDay = useAppSelector((state) =>
    selectProductOfDayByGoodsType(state, element?.key as ResourceTypeText),
  );

  const dispatch = useAppDispatch();
  const location = useLocation();
  useEffect(() => {
    // if (!location.pathname.includes(AppRoute.STOCK)) ;
    setElement(sellModalParams);
    // return (): void => {
    //   setOpenSellModal(false);
    // };
  }, [location.pathname, sellModalParams]);

  useEffect(() => {
    onClose();
  }, [location.pathname]);

  useEffect(() => {
    if (element) {
      const value = element.value.value;

      if (value >= 10000) setStep(1000);

      if (value < 10000) setStep(100);

      if (value < 1000) setStep(10);

      if (value < 100) setStep(1);
    }

    if (element && sellModalParams?.achieveValue) {
      setAmount(Math.min(sellModalParams?.achieveValue, element.value.value));
      setSliderValue(calculateSliderValue(sellModalParams?.achieveValue || 0));
    }
  }, [element]);

  const totalPrice = element?.value && amount * element?.value.costPerUnit;

  const calculateSliderValue = (resourceAmount: number): number => {
    if (!element) return 0;
    const maxValue = element.value.value;

    return (resourceAmount / maxValue) * 100;
    // return +((resourceAmount / maxValue) * 100).toFixed(2);
    // return Math.round((resourceAmount / maxValue) * 100);
  };

  const handleSliderChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    if (!element) return;
    setActivePercent(null);

    if (isDragging) return;
    const rawValue = Number(event.target.value);
    const maxValue = element.value.value;
    let adjustedValue = Math.round(rawValue / step) * step;

    if (adjustedValue < 0) {
      adjustedValue = 0;
    } else if (adjustedValue > element.value.value) {
      adjustedValue = element.value.value;
    }

    if (maxValue - adjustedValue < step) {
      adjustedValue = maxValue;
    }
    const newAmount = adjustedValue;
    setAmount(newAmount);
    setSliderValue(calculateSliderValue(newAmount));
    setTargetSliderValue(calculateSliderValue(newAmount));
  };

  const handleTouchStart = (
    event: React.TouchEvent<HTMLInputElement>,
  ): void => {
    setIsDragging(true);
    const touch = event.touches[0];
    setStartX(touch.clientX);
    setInitialAmount(amount);
  };
  const names = storageTextObj(t);
  const icon = element?.key ? getIcon(element.key) : null;
  const handleTouchEnd = (): void => {
    // setIsDragging(false);
    // setTargetAmount(amount);
    setTargetSliderValue(sliderValue);
  };
  const handleTouchMove = (event: React.TouchEvent<HTMLInputElement>): void => {
    if (!isDragging) return;

    if (!element) return;

    const touch = event.touches[0];
    const rangeElement = event.target as HTMLInputElement;
    const offsetX = touch.clientX - startX;
    const newValue =
      initialAmount +
      (offsetX / rangeElement.clientWidth) * element.value.value;
    let adjustedValue = Math.round(newValue / step) * step;

    if (adjustedValue < 0) {
      adjustedValue = 0;
    } else if (adjustedValue > element.value.value) {
      adjustedValue = element.value.value;
    }
    setSliderValue(calculateSliderValue(adjustedValue));
    setTargetSliderValue(calculateSliderValue(adjustedValue));
    setAmount(adjustedValue);
  };

  const handleClickPercent = (percent: number): void => {
    if (element && element.value && element.value.value) {
      playSound('buttons/switch');
      const maxValue = element.value.value;
      const calculatedAmount = Math.floor((percent / 100) * maxValue);

      const newAmount = Math.min(calculatedAmount, maxValue);
      setActivePercent(percent);
      setAmount(newAmount);
      setSliderValue(calculateSliderValue(newAmount));
      setTargetSliderValue(percent);
    }
  };

  const onClose = (): void => {
    setOpenSellModal(false);
    setElement(null);
    setActivePercent(null);
    setSellModalParams(null);
    setAmount(0);
    setSliderValue(0);
  };

  const onSell = (): void => {
    setSellIsLoading(true);
    sellStorageItem([{ resource: element?.key as ResourceTypeText, amount }])
      .unwrap()
      .then(() => {
        playSound('buttons/sell');
        onClose();
        toast(
          <Notification
            icon={() => <CurrencyImage kind="cash" size={'2.5rem'} />}
            text={t('The item has been successfully sold.')}
          />,
          defaultToastConfig,
        );
      })
      .catch(() => {
        onClose();
        toast(
          <Notification
            className="justify-center"
            text={t('Item sale error')}
          />,
          defaultToastConfig,
        );
      })
      .finally(() => {
        localStorage.setItem('lastSellTime', new Date().toISOString());
        dispatch(setTimeToNextSell(10));
        setSellIsLoading(false);
      });
  };

  useEffect(() => {
    const animate = (): void => {
      setSliderValue((prevValue) => {
        if (Math.abs(prevValue - targetSliderValue) <= 0.1) {
          cancelAnimationFrame(animationRef.current!);

          return targetSliderValue;
        }
        const direction = prevValue < targetSliderValue ? 0.1 : -0.1;
        // const stepSize = Math.abs(prevValue - targetSliderValue) > 5 ? 5 : 1;

        return prevValue + direction;
      });
      animationRef.current = requestAnimationFrame(animate);
    };
    animationRef.current = requestAnimationFrame(animate);

    return (): void => cancelAnimationFrame(animationRef.current!);
  }, [targetSliderValue]);

  return (
    <div
      className={`modalUpdateBackground ${openSellModal ? 'open' : ''} isolate z-[999]`}
    >
      <CSSTransition
        in={openSellModal}
        timeout={300}
        classNames="my-node-modal"
        unmountOnExit
        onExited={() => {
          setElement(null);
          setActivePercent(null);
          setSellModalParams(null);
        }}
      >
        <div className="modalSell">
          <button
            data-fancybox-close=""
            className="f-button is-close-btn"
            style={{ background: 'var(--white-10, #FFFFFF1A)' }}
            title="Close"
            onClick={onClose}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              style={{ strokeWidth: '4px' }}
            >
              <path d="M20 20L4 4m16 0L4 20"></path>
            </svg>
          </button>
          {element && (
            <>
              <span className="top-gradient"></span>
              <div className="sell-modal__head">
                <h2 className="popup-title px-14">
                  {names[element.key]}
                  {/* {resources[element.key as ResourceTypeText].nameMaterial} */}
                </h2>
              </div>
              <div className="sell-modal__body">
                <div className="sell-modal__items">
                  <div className="sell-item">
                    <div
                      className="sell-item__icon"
                      onClick={(): void => goToProduct(element.key)}
                    >
                      {/* <picture>
                        <source
                          srcSet={
                            element.key === 'wood'
                              ? resourceImages[element.key as ResourceTypeText]
                                  .icon.webpRight
                              : icon?.webp
                          }
                          type="image/webp"
                        />
                        <img
                          src={
                            element.key === 'wood'
                              ? resourceImages[element.key as ResourceTypeText]
                                  .icon.pngRight
                              : icon?.png
                          }
                          width={80}
                          alt="resource"
                        />
                      </picture> */}
                      <div className="size-20 relative bg-white/10 rounded-lg overflow-hidden">
                        {productOfDay && (
                          <ContractBackground
                            type={
                              productOfDay?.type === 'premium'
                                ? 'purple'
                                : 'green'
                            }
                          />
                        )}
                        <Picture
                          img={
                            element.key === 'wood'
                              ? resourceImages[element.key as ResourceTypeText]
                                  .icon.pngRight
                              : icon?.png
                          }
                          webp={
                            element.key === 'wood'
                              ? resourceImages[element.key as ResourceTypeText]
                                  .icon.webpRight
                              : icon?.webp
                          }
                          alt={element.key}
                          className="size-full block"
                        />
                      </div>
                    </div>
                    <div className="sell-item__content">
                      <p>{t('Available In stock:')}</p>
                      <span>
                        {element.value.value} {t('units')}
                      </span>
                    </div>
                  </div>
                  <div className="sell-item-counter">
                    <div
                      className="sell-item-counter-button"
                      onClick={() => {
                        playSound('buttons/switch');
                        const newAmount = Math.max(amount - step, 0);
                        setAmount(newAmount);
                        setSliderValue(calculateSliderValue(newAmount));
                        setActivePercent(null);
                      }}
                    >
                      <svg id="icon-Vector-Stroke-1" viewBox="0 0 78 32">
                        <path d="M43.843 6.443l18.96 0c2.754 0 4.986 2.232 4.986 4.986s-2.232 4.986-4.986 4.986l-18.96-0c-6.129 0-4.387 0-9.972-0l-18.96-0c-2.754 0-4.986-2.232-4.986-4.986s2.232-4.986 4.986-4.986l18.96 0c4.52-0 4.986 0 9.972 0z"></path>
                      </svg>
                    </div>
                    <div className="sell-item-counter-text">
                      <p>{amount}</p>
                      <span>
                        1 {names[element.key]} = {element?.value.costPerUnit}{' '}
                        Cash
                      </span>
                    </div>
                    <div
                      className="sell-item-counter-button"
                      onClick={() => {
                        playSound('buttons/switch');
                        const newAmount = Math.min(
                          amount + step,
                          element.value.value,
                        );
                        setAmount(newAmount);
                        setSliderValue(calculateSliderValue(newAmount));
                        setActivePercent(null);
                      }}
                    >
                      <svg id="icon-Vector-Stroke" viewBox="0 0 32 32">
                        <path d="M16 2.205c1.134 0 2.053 0.919 2.053 2.053v7.807l7.807 0c1.134 0 2.053 0.919 2.053 2.053s-0.919 2.053-2.053 2.053h-7.807l0 7.807c0 1.134-0.919 2.053-2.053 2.053s-2.053-0.919-2.053-2.053v-7.807h-7.807c-1.134 0-2.053-0.919-2.053-2.053s0.919-2.053 2.053-2.053h7.807l-0-7.807c0-1.134 0.919-2.053 2.053-2.053z"></path>
                      </svg>
                    </div>
                  </div>
                </div>
                <div className="sell-modal__range">
                  <div className="range-container">
                    <div
                      className="progress-track"
                      style={{
                        width: `${sliderValue}%`,
                      }}
                    ></div>
                    <input
                      className="range"
                      type="range"
                      min="0"
                      // step={step}
                      max="100"
                      value={sliderValue}
                      onTouchStart={handleTouchStart}
                      onTouchEnd={handleTouchEnd}
                      onTouchMove={handleTouchMove}
                      onChange={handleSliderChange}
                    />
                  </div>
                </div>
                <div className="sell-modal-percent">
                  {[25, 50, 75, 100].map((percent, index) => (
                    <div
                      key={index}
                      className={`percent-block ${activePercent === percent ? 'active' : ''}`}
                      onClick={() => handleClickPercent(percent)}
                    >
                      {percent}%
                    </div>
                  ))}
                </div>
                <div className="sell-modal-amount">
                  <span>You will get</span>
                  <p className="flex items-center">
                    <Picture
                      className="w-10 h-10"
                      img={cashPng}
                      webp={cashWebp}
                      alt="cash"
                    />
                    {/* <picture>
                      <source srcSet="/img/icons/cash.webp" type="image/webp" />
                      <img src="/img/icons/cash.png" alt="icon" width={40} />
                    </picture> */}
                    {totalPrice ? toShortNumFormat(totalPrice) : 0}
                  </p>
                </div>
                <MainButton
                  isLoading={sellIsLoading}
                  inactive={!amount || timeToNextSell > 0}
                  disabled={!amount || sellIsLoading || timeToNextSell > 0}
                  text={timeToNextSell > 0 ? '' : t('Sell')}
                  // className={
                  //   timeToNextSell > 0 ? '' : ''
                  // }
                  onClick={onSell}
                >
                  {timeToNextSell > 0 && (
                    <div className="w-[115px] flex items-center justify-between">
                      <Loader />
                      <span className="font-semibold text-lg leading-0 italic">
                        {timeStringFormat(timeToNextSell * 1000, {
                          template: '{minutes}m : {seconds}s',
                        })}
                      </span>
                    </div>
                  )}
                </MainButton>
                {/* <button
                  className={`btn btn--full ${!amount ? 'btn-disable' : ''}`}
                  type="button"
                  onClick={onSell}
                >
                  {}
                </button> */}
              </div>
            </>
          )}
        </div>
      </CSSTransition>
    </div>
  );
};

export default ModalSellResource;
